// The Knocker: the alien's revealed shape at the door (sprint 33 item 1), read from the Aseprite sheets in
// client/art/aseprite/knocker-reveal/ (built by art/aseprite/src/knocker_reveal.lua, pixel for pixel the owner-approved A4
// alien frames). Nothing is drawn here: every pixel is cut out of the exported per-layer sheets of the `glass-reveal` tag.
// The sheets are in glass px (180 x 166, the door's GLASS), the Knocker's base on row SHEET_BASE (A4's sum slab).
// One bit: the sheet's ink and its dark red are ink, its cream is paper, its red is the red accent (art bible).
import { decodeIndexed } from './eyes.js';
import { FAILS } from '../game/fails.js';

export const FPS = 15;
export const SHEET_W = 180, SHEET_H = 166;
export const SHEET_BASE = 128; // the row the dome sits on in the sheets (A4's sum slab)
// the layers, bottom to top, as in knocker-reveal.aseprite; the hands are kept apart (they hook over the rule bar or the sum)
export const BODY_LAYERS = ['dome', 'bloom', 'eyes', 'cores', 'mouth', 'teeth'];
export const HAND_LAYER = 'hands';
// palette index (L2, art/aseprite/src/palettes.lua) -> one bit: 1 ink, 2 paper, 3 red
export const ONE_BIT = { 1: 1, 10: 2, 12: 1, 13: 3 };
// the palette colours those indices must have (checked in test/knocker.test.js)
export const ONE_BIT_RGB = { 1: [11, 13, 26], 10: [246, 236, 210], 12: [110, 20, 38], 13: [226, 64, 52] };
// the frame the reveal holds on, with the eyes straight ahead (A4's 1000 ms grin): reduced motion shows only this one
export const REST_FRAME = 3;
// the reveal: the alien's eyes go red on the person first (reactions.js, frame 8), then at KNOCKER_AT the head becomes the Knocker
export const KNOCKER_AT = 13;

const BASE = new URL('./art/aseprite/knocker-reveal/', import.meta.url);

// one tag of the per-layer sheets -> { frames, steps, body(f), hands(f), layer(name, f) } with Uint8Array(180 * 166) one-bit codes (0 clear)
export function parseKnocker(layers, durations) {
  const n = durations.length, body = [], hands = [], each = {}, darks = [];
  for (let f = 0; f < n; f++) {
    const b = new Uint8Array(SHEET_W * SHEET_H), h = new Uint8Array(SHEET_W * SHEET_H), dk = new Uint8Array(SHEET_W * SHEET_H);
    for (const name of [...BODY_LAYERS, HAND_LAYER]) {
      const L = layers[name];
      if (!L) continue;
      const out = name === HAND_LAYER ? h : b, one = new Uint8Array(SHEET_W * SHEET_H);
      for (let y = 0; y < SHEET_H; y++) for (let x = 0; x < SHEET_W; x++) {
        const i = L.index[y * L.w + f * SHEET_W + x];
        if (i && ONE_BIT[i]) out[y * SHEET_W + x] = one[y * SHEET_W + x] = ONE_BIT[i];
        if (i === DARK_RED && name !== HAND_LAYER) dk[y * SHEET_W + x] = 1; else if (i && name !== HAND_LAYER) dk[y * SHEET_W + x] = 0;
      }
      (each[name] ??= [])[f] = one;
    }
    body.push(b); hands.push(h); darks.push(dk);
  }
  const steps = [];
  durations.forEach((ms, f) => { for (let k = Math.max(1, Math.round(ms * FPS / 1000)); k > 0; k--) steps.push(f); });
  // the columns each hand takes in any frame (sheet x, left hand then right): what must stay in view over the slab
  const spans = [[1e9, -1], [1e9, -1]];
  for (const h of hands) for (let y = 0; y < SHEET_H; y++) for (let x = 0; x < SHEET_W; x++) if (h[y * SHEET_W + x]) {
    const s = spans[x < SHEET_W / 2 ? 0 : 1]; s[0] = Math.min(s[0], x); s[1] = Math.max(s[1], x);
  }
  return { frames: n, steps, body: f => body[f], hands: f => hands[f], layer: (name, f) => each[name]?.[f], dark: f => darks[f],
    handSpans: spans.filter(([a, b]) => b >= a) };
}

// Where a box `w` wide may sit on a strip from `lo` to `hi` (exclusive), as near `x` as it can, clear of the column spans
// `avoid` ([x0, x1] inclusive) with a column of gap. The phone's jack-o'-lantern keeps off the Knocker's fingers this way
// (sprint 33 item 1 fix pass). No room: `x` as it was.
export function clearOf(x, w, avoid, lo, hi) {
  const ok = c => c >= lo && c + w <= hi && avoid.every(([a, b]) => c + w <= a - 1 || c >= b + 2);
  if (ok(x)) return x;
  const cands = avoid.flatMap(([a, b]) => [b + 2, a - 1 - w]).filter(ok).sort((p, q) => Math.abs(p - x) - Math.abs(q - x));
  return cands.length ? cands[0] : x;
}
// the sheet's dark red (L2 red2, the outer bloom): ink in one bit, red2 on a level 2 night door (night.js)
export const DARK_RED = 12;

const isNode = !!globalThis.process?.versions?.node;
async function bytes(url) {
  if (isNode) { const fs = 'node:fs/promises'; return new Uint8Array(await (await import(fs)).readFile(url)); }
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return new Uint8Array(await r.arrayBuffer());
}
export async function loadKnocker(tag = 'glass-reveal') {
  const layers = {};
  let durations = null;
  await Promise.all([...BODY_LAYERS, HAND_LAYER].map(async name => {
    const file = `knocker-reveal-${tag}-${name}-sheet`;
    layers[name] = await decodeIndexed(await bytes(new URL(file + '.png', BASE)));
    const json = JSON.parse(new TextDecoder().decode(await bytes(new URL(file + '.json', BASE))));
    durations ??= json.frames.map(f => f.duration);
  }));
  return parseKnocker(layers, durations);
}

// The sheet the game draws from. A missing sheet (offline, a 404) leaves the alien's old reveal, never a Knocker redrawn in code.
let sheet = null;
try { sheet = await loadKnocker(); } catch (e) { console.warn('knocker:', e.message); }
export const knockerSheet = () => sheet;
export const setKnockerSheet = s => { sheet = s; };
// the wrong-Open scream (sprint 33 item 2): the same sheets' lunge tag (the rush, the impact squash, the scream with three-finger palms
// flat on the glass), approved in A3. Without it the wrong Open keeps the old lunge.
let screamS = null;
try { screamS = await loadKnocker('lunge'); } catch (e) { console.warn('knocker scream:', e.message); }
export const screamSheet = () => screamS;
export const setScreamSheet = s => { screamS = s; };
// the scream sheet's frame `rf` game frames after the land (null: not yet, or no sheet). The rush is the first frame after the land's.
export function screamFrame(rf, skin = null) {
  const k = skinSheet(skin) ?? screamS;
  if (!k || rf < 1) return null;
  const s = k.steps;
  return s[Math.min(s.length - 1, rf - 1)];
}

// The scream's stand-ins (game/fails.js), cut from their own sheets (art/aseprite/src/knocker_skins.lua) on the lunge's timing.
// They load after the page, so a missing or late sheet leaves the Knocker's scream.
const SKIN_BASE = new URL('./art/aseprite/knocker-skins/', import.meta.url);
export const SKIN_LAYERS = ['dome', 'bloom', 'eyes'];
export async function loadSkin(name) {
  const layers = {};
  let durations = null;
  await Promise.all(SKIN_LAYERS.map(async layer => {
    const file = `knocker-skins-${name}-${layer}-sheet`;
    layers[layer] = await decodeIndexed(await bytes(new URL(file + '.png', SKIN_BASE)));
    const json = JSON.parse(new TextDecoder().decode(await bytes(new URL(file + '.json', SKIN_BASE))));
    durations ??= json.frames.map(f => f.duration);
  }));
  return parseKnocker(layers, durations);
}
const skins = {};
export const skinsReady = Promise.all(FAILS.map(async n => {
  try { skins[n] = await loadSkin(n); } catch (e) { console.warn(`knocker skin ${n}:`, e.message); }
}));
export const skinSheet = name => (name && skins[name]) || null;

// Which Knocker frame shows `f` game frames after the reveal (null: not yet). Reduced motion: the resting frame, still,
// from the reveal's first frame (no KNOCKER_AT wait: the card already says "was a visitor"; sprint 33 item 1 fix pass)
export function knockerFrame(f, reduced = false) {
  if (!sheet || f < 0) return null;
  if (reduced) return REST_FRAME;
  if (f < KNOCKER_AT) return null;
  const s = sheet.steps;
  return s[Math.min(s.length - 1, f - KNOCKER_AT)];
}

// Paint a Knocker layer set into the raster B, the glass's top-left at (gx, gy), its base on glass row `base`.
// keep(u, v): which glass pixels may be painted (the arch). Returns how many pixels it painted.
// dark: the frame's dark-red pixels (sheet.dark(f)), marked for a night door (bits.js `art`; one bit shows them as ink).
export function paintKnocker(B, gx, gy, codes, base, keep = () => true, dark = null, darkArt = 0) {
  const { px, S } = B, dy = base - SHEET_BASE, art0 = S.art ?? 0;
  let n = 0;
  for (let y = 0; y < SHEET_H; y++) for (let x = 0; x < SHEET_W; x++) {
    const c = codes[y * SHEET_W + x];
    if (!c) continue;
    const u = x, v = y + dy;
    if (!keep(u, v)) continue;
    S.tint = c === 3 ? 2 : 0; // T.red
    if (dark) S.art = dark[y * SHEET_W + x] ? darkArt : art0;
    px(gx + u, gy + v, c === 1 ? 0 : 1);
    n++;
  }
  S.tint = 0;
  if (dark) S.art = art0;
  return n;
}
